import {
  BorderStyle,
  Document,
  ImageRun,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  WidthType,
  TextRun,
  ExternalHyperlink
} from 'docx';
import { ParsedTableData, getSourcesList, prepareContentForTable } from '../ChatReply/ChatReply';
import { downloadFilenameGenerator, sanitizeHeaderText } from '.';
import LABELS from '../../../ui-constants/en.json';
import AppConst from '../../../ui-constants/appConstants.json';
import { publicSources } from '../../../label.constant';
import { decodeHTML, getResponseSourceType } from '../../../utils/commonUtils';

import {
  IQuery,
  IQueryContent,
  RESPONSE_BY,
  RESPONSE_TYPE,
  ItableCntentProps
} from '../../../models/IQueryType';

const noBorder = {
  style: BorderStyle.NONE,
  size: 0,
  color: "ff0000",
};
const singleBorder = {
  style: BorderStyle.SINGLE,
  size: 2,
  color: "e8e8ee",
};
const noBorders = {
  top: noBorder,
  bottom: noBorder,
  left: noBorder,
  right: noBorder,
};

const tableCellStyling = {
  borders: {
    top: singleBorder,
    bottom: singleBorder,
    left: singleBorder,
    right: singleBorder,
  },
  shading: {
    color: "f7f7f8",
    fill: "000000",
    type: ShadingType.SOLID,
  },
};
const emptyRow = new TableRow({
  children: [
    new TableCell({
      borders: noBorders,
      children: [new Paragraph("")],
    }),
  ],
});

// To make bold the first row
let rowIndex = 0;
let isFirstRow = false;
const separatorOnlyRegex = /^\|(?:-+\|)+$/;

const fixMalformedUrl = (url: string): string => {
  // Only fix missing colon after protocol - don't modify anything else
  url = url.replace(/^(https?)(\/\/)/g, '$1:$2');  
  url = url.replace(/^(https?):\/{1}([^/])/g, '$1://$2');
  if (url.startsWith('www.') && !url.includes('://')) {
    url = 'https://' + url;
  }  
  return url;
};

// Fixed parseMarkdownLinks function that returns proper hyperlink elements & text
const parseMarkdownLinks = (
  cellText: string,
  isBold: boolean = false
): (TextRun | ExternalHyperlink)[] => {
  // Combined regex to match both markdown links and standalone URLs
  const combinedRegex = /(\[([^\]]+)\]\(([^)]+)\))|(https?\/\/[^\s\)]+)/g;
  let parts: (TextRun | ExternalHyperlink)[] = [];
  let lastIndex = 0;
  let match;
  
  if (!cellText || cellText.trim().length === 0) {
    return [new TextRun({ text: cellText || '', size: 20, bold: isBold })];
  }
  
  // Reset regex lastIndex
  combinedRegex.lastIndex = 0;
  
  // Check if there are any links (markdown or standalone)
  const hasLinks = combinedRegex.test(cellText);
  combinedRegex.lastIndex = 0; // Reset after test
  
  if (!hasLinks) {
    return [new TextRun({ text: cellText, size: 20, bold: isBold })];
  }
  
  while ((match = combinedRegex.exec(cellText)) !== null) {
    // Add preceding plain text
    if (match.index > lastIndex) {
      const plainText = cellText.substring(lastIndex, match.index);
      if (plainText) {
        // Handle bold segments in plain text (existing logic)
        const boldSegmentRegex = /\*\*(.*?)\*\*/g;
        let lastBoldIndex = 0;
        let boldMatch;
        let hasMatches = false;
        
        while ((boldMatch = boldSegmentRegex.exec(plainText)) !== null) {
          hasMatches = true;
          
          if (boldMatch.index > lastBoldIndex) {
            const beforeText = plainText.substring(lastBoldIndex, boldMatch.index);
            if (beforeText) {
              parts.push(new TextRun({ 
                text: beforeText, 
                size: 20,
                bold: isBold
              }));
            }
          }
          
          parts.push(new TextRun({ 
            text: boldMatch[1], 
            size: 20,
            bold: true
          }));
          
          lastBoldIndex = boldMatch.index + boldMatch[0].length;
        }
        
        if (lastBoldIndex < plainText.length) {
          const remainingText = plainText.substring(lastBoldIndex);
          if (remainingText) {
            parts.push(new TextRun({ 
              text: remainingText, 
              size: 20,
              bold: isBold
            }));
          }
        }
        
        if (!hasMatches) {
          parts.push(new TextRun({ 
            text: plainText, 
            size: 20,
            bold: isBold
          }));
        }
      }
    }    
    // Process the matched link
    if (match[1]) {
      const linkText = match[2].trim();
      let url = match[3].trim();
      
      url = fixMalformedUrl(url.trim());
      if (url && linkText) {
        try {
          const urlObj = new URL(url);
          if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
            throw new Error('Invalid protocol');
          }
          
          parts.push(new ExternalHyperlink({
            children: [
              new TextRun({
                text: linkText,
                size: 20,
                color: '0563c1',
                underline: {}
              })
            ],
            link: url
          }));
        } catch (error) {
          console.warn(`Invalid URL found: ${url}`, error);
          parts.push(new TextRun({
            text: `[${linkText}](${match[3]})`,
            size: 20
          }));
        }
      }
    } else if (match[4]) {
      let url = match[4].trim();
      url = fixMalformedUrl(url.trim());
      try {
        const urlObj = new URL(url);
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
          throw new Error('Invalid protocol');
        }        
        const displayText = url.replace(/^https?:\/\//, '').split('/')[0];
        
        parts.push(new ExternalHyperlink({
          children: [
            new TextRun({
              text: displayText,
              size: 20,
              color: '0563c1',
              underline: {}
            })
          ],
          link: url
        }));
      } catch (error) {
        console.warn(`Invalid standalone URL found: ${url}`, error);
        parts.push(new TextRun({
          text: match[4],
          size: 20
        }));
      }
    }    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining plain text
  if (lastIndex < cellText.length) {
    const remainingText = cellText.substring(lastIndex);
    if (remainingText) {
      parts.push(new TextRun({ 
        text: remainingText, 
        size: 20 
      }));
    }
  }
  
  return parts;
};

export const parseMarkdownTableToDocxTable = (
  markdown: string,
  isBold: boolean = false
): Table | null => {
  const line = markdown.trim().split('\n')[0];
  const row = line
    .split('|')
    .map(h => h.trim())
    .filter(Boolean);

   const cells = row.map(cellText => {
    const elements = parseMarkdownLinks(cellText,isBold);    
    return new TableCell({
      children: [
        new Paragraph({
          children: elements
        })
      ]
    });
  });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    layout: TableLayoutType.AUTOFIT,
    rows: [new TableRow({ children: cells })],
  });
};

const isMarkdownTableLine = (line: string) => /^\|(?:\s*[^|\n]+\s*\|)+\s*$/.test(line) && !/^\|\s*-+/.test(line);

const generateTable = (tableContent: ParsedTableData | null) => {
  if (tableContent) {
    const headersCells: TableCell[] = [];
    const valueRows: TableRow[] = [];
    tableContent.headers.map((header) =>
      headersCells.push(
        new TableCell({
          ...tableCellStyling,

          children: [
            new Paragraph({
              text: sanitizeHeaderText(header),
              style: "tableHeader",
              spacing: {
                after: 100,
              },
            }),
          ],
        })
      )
    );

    tableContent.values.forEach((valArr: any) => {
      const cells: TableCell[] = [];
      valArr.forEach((val: ItableCntentProps) => {
        cells.push(
          new TableCell({
            ...tableCellStyling,
            children: [new Paragraph({ text: `${val.val}`, style: "summary" })],
          })
        );
      });
      valueRows.push(
        new TableRow({
          children: cells
        })
      );
    });

    return new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE
      },
      layout: TableLayoutType.AUTOFIT,
      rows: [
        // Header
        new TableRow({
          tableHeader: true,
          children: headersCells
        }),
        ...valueRows
      ]
    });
  } else {
    return new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE
      },
      layout: TableLayoutType.AUTOFIT,
      rows: [emptyRow]
    });
  }
};

const generateTranscriptTable = (tableContent: any, source: string) => {
  if (tableContent?.length && tableContent?.length > 0) {
    const headersCells: TableCell[] = [];
    const valueRows: TableRow[] = [];

    headersCells.push(
       new TableCell({
        ...tableCellStyling,
        children: [
          new Paragraph({
            text: sanitizeHeaderText('Year(s)'),
            style: 'tableHeader',
            spacing: {
              after: 100
            }
          })
        ]
      }),
      new TableCell({
        ...tableCellStyling,
        children: [
          new Paragraph({
            text: sanitizeHeaderText('Sector(s)'),
            style: 'tableHeader',
            spacing: {
              after: 100
            }
          })
        ]
      }),
    );

    // Create data rows
    tableContent.forEach((data: any) => {
      const cells: TableCell[] = [];
          const sectorsText = data.sectors && data.sectors.length > 0 
        ? data.sectors.join(', ') 
        : '';
      const yearsText = data.years && data.years.length > 0 
        ? data.years.join(', ') 
        : '';

      cells.push(
        new TableCell({
          ...tableCellStyling,
          children: [new Paragraph({ text: yearsText })]
        }),

         new TableCell({
          ...tableCellStyling,
          children: [new Paragraph({ text: sectorsText })]
        }),
      );

      valueRows.push(
        new TableRow({
          children: cells
        })
      );
    });

    return new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE
      },
      layout: TableLayoutType.AUTOFIT,
      rows: [
        new TableRow({
          tableHeader: true,
          children: headersCells
        }),
        ...valueRows,
        emptyRow,
        emptyRow
      ]
    });
  } else {
    return new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE
      },
      layout: TableLayoutType.AUTOFIT,
      rows: [emptyRow]
    });
  }
}

const generateSectorTable = (tableContent: any, source: string) => {
  if (tableContent?.length && tableContent?.length > 0) {
    const headersCells: TableCell[] = [];
    const valueRows: TableRow[] = [];

    headersCells.push(
      new TableCell({
        ...tableCellStyling,

        children: [
          new Paragraph({
            text: sanitizeHeaderText(
              source === 'MarketReports' ? 'Document Title' : 'Relevant Document(s)'
            ),
            style: 'tableHeader',
            spacing: {
              after: 100
            }
          })
        ]
      }),
      new TableCell({
        ...tableCellStyling,

        children: [
          new Paragraph({
            text: sanitizeHeaderText(source === 'MarketReports' ? 'Page nos.' : 'Author(s)'),
            style: 'tableHeader',
            spacing: {
              after: 100
            }
          })
        ]
      })
    );
    if (source === 'MarketReports') {
      headersCells.push(
        new TableCell({
          ...tableCellStyling,
          children: [
            new Paragraph({
              text: sanitizeHeaderText('Source'),
              style: 'tableHeader',
              spacing: {
                after: 100
              }
            })
          ]
        })
      );
    }
    tableContent.forEach((data: any) => {
      const cells: TableCell[] = [];
      let fileNameContent = '';
      let fileParts = '';
      let pageNo = '';
      if (data.page_number.length > 0) {
        fileNameContent = data.file_name;
        fileParts = data.page_number.length > 1 ? '(Slides ' : '(Slide ';
      }
      data.page_number.length &&
        data.page_number.forEach((numer: any, i: number) => {
          fileParts = fileParts + numer + (i === data.page_number.length - 1 ? '' : ', ');
          pageNo = pageNo + numer + (i === data.page_number.length - 1 ? '' : ', ');
        });
      fileParts = fileParts + ')';

      cells.push(
        new TableCell({
          ...tableCellStyling,
          children: [
            new Paragraph({ text: fileNameContent }),
            new Paragraph({ text: source === 'MarketReports' ? '' : fileParts })
          ]
        })
      );
      if (source === 'MarketReports') {
        cells.push(
          new TableCell({
            ...tableCellStyling,
            children: [new Paragraph({ text: pageNo })]
          })
        );
      }
      let authorData: string = data.authors.reduce((str: any, author: any) => {
        if (str === '') {
          str = author?.author_name;
        } else {
          str = str + ', ' + author?.author_name;
        }
        return str;
      }, '');
      cells.push(
        new TableCell({
          ...tableCellStyling,
          children: [
            new Paragraph({
              text: source === 'MarketReports' ? data?.source : authorData
            })
          ]
        })
      );
      valueRows.push(
        new TableRow({
          children: cells
        })
      );
    });

    return new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      layout: TableLayoutType.AUTOFIT,
      rows: [
        // Header
        new TableRow({
          tableHeader: true,
          children: headersCells,
        }),
        ...valueRows,
        emptyRow,
      ],
    });
  } else {
    return new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE
      },
      layout: TableLayoutType.AUTOFIT,
      rows: [emptyRow]
    });
  }
};

const seTokenError = (replyData: string) => {
  if (
    replyData?.includes(LABELS.TOKEN_ERR) ||
    replyData?.includes("https://aka.ms/oai/quotaincrease") ||
    replyData?.includes("SQL_ERROR")
  ) {
    return true;
  } else {
    return false;
  }
};

const getSources = (values: { Sources: any; summary: string }) => {
  if (values?.Sources || values.summary?.includes("Information_Sources:")) {
    let sourcesFromChat: string = "";
    if (values.summary?.includes("Information_Sources:")) {
      const dataSplitted = values.summary?.split("Information_Sources:");
      values.summary = dataSplitted[0] as any;
      sourcesFromChat = dataSplitted[1];
    }
    return getSourcesList(sourcesFromChat, values?.Sources || []);
  }
};

const handleSourceSection = (
  sourceArray: string[],
  checkedCheckboxes?: any,
  eyIPInterRadio?: any,
  validURLinputValue?: any
) => {
  let sourceType = '';
  if (checkedCheckboxes && checkedCheckboxes.length > 0) {
    if (checkedCheckboxes.length === 1 && checkedCheckboxes.includes(publicSources[0]))
      sourceType = publicSources[0];
    else if (checkedCheckboxes.length === 1 && checkedCheckboxes.includes(publicSources[1]))
      sourceType = publicSources[1];
    else if (checkedCheckboxes.length === 1 && checkedCheckboxes.includes(publicSources[2]))
      sourceType = publicSources[2];
    else if (
      checkedCheckboxes.length === 2 &&
      checkedCheckboxes.includes(publicSources[0]) &&
      checkedCheckboxes.includes(publicSources[1])
    )
      sourceType = 'Combined';
  }
  if (eyIPInterRadio) {
    sourceType = eyIPInterRadio;
  }
  let modifiedSourceArray;
  if(sourceType==='Surveys'){
    if(sourceArray?.length>0 && sourceArray[0].includes('Surveys')) {
      modifiedSourceArray = ['EY conducted surveys'];
     }
     else{
       modifiedSourceArray = [...sourceArray];
     }
  }
  else{
    let getSources = validURLinputValue ? validURLinputValue : getResponseSourceType(sourceType);
    sourceArray.shift();
    modifiedSourceArray = [getSources, ...sourceArray];
  }
  if (modifiedSourceArray && modifiedSourceArray.length > 0) {
    return modifiedSourceArray;
  }
  return 'Secondary research';
};

const parseFormattedLineChildren = (line: string, isBold = false): (TextRun | ExternalHyperlink)[] => {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  const result: (TextRun | ExternalHyperlink)[] = [];  
  parts.filter(Boolean).forEach((part) => {
    const boldMatch = part.match(/^\*\*(.*)\*\*$/);
    if (boldMatch) {
      // Don't remove hyphens from bold text
      result.push(new TextRun({ text: boldMatch[1], bold: true }));
    } else {
      // Don't remove hyphens from regular text - URLs need them
      const elements = parseMarkdownLinks(part, isBold);
      result.push(...elements);
    }
  });  
  return result;
};

const generateDocSummary = (
  line: string,
  type: string,
  totalLength: number = 0,
  ind: number,
  isParitallyAbortedResponse: boolean,
  isCompletelyAbortedResposne: boolean,
  sourceSection?: any,
  source?: any,
) => {
  let modifiedSource: any = [];
  // modifiedSource.push(new TextRun({ break: 1 }))
  const pathName = window.location.pathname;
  sourceSection.map((source: any, index: number) => {
    if (isParitallyAbortedResponse && index === 0) {
      modifiedSource.push(new TextRun({
        text: LABELS.chat_reply.PARTIAL_STOP_GENERATION_MSG,
        break: 1,
        color: '656579',
        italics: true
      }))
    }
    if(pathName=="/surveyChatPage") {
      if(!sourceSection[0] || sourceSection[0]?.trim()==='EY conducted surveys'){
        modifiedSource.push(new TextRun({
          text: index === 0 ? `Source: ${source}` : `${source}`,
          // break: 1
        })
        )
      }
      else
     {
      modifiedSource.push(new TextRun({
        text: index === 0 ? `Source: ${source}` : `${source}`,
        // break: 1
      })
      )
     }
    }
    else{
    modifiedSource.push(new TextRun({
      text: index === 0 ? `Source: ${source}` : `${source}`,
      // break: 1
    })
    );
  }
  });
  if (!sourceSection || !sourceSection.length) {
    // Add a line break only when sourceSection is empty or not properly generated
    modifiedSource.push(new TextRun({ break: 1 }));
  }

  if (seTokenError(line)) {
    return new Paragraph({
      children: [
        new TextRun({ text: LABELS.chat_reply.EMBROYNIC_ERROR }),
        new TextRun({ text: '', break: 1 }),
        ...modifiedSource
      ]
    });
  }

  if (line.startsWith(LABELS.SORRY_ERR) && !line.startsWith(LABELS.SORRY_ERR1)) {
    return new Paragraph({
      children: [
        new TextRun({ text: LABELS.GENERIC_OOPS_ERROR }),
        new TextRun({
          text: ' Source: Secondary research',
          break: 1
        })
      ]
    });
  }

  if (RESPONSE_BY.OPENAI === type && line?.includes('Information_Sources')) {
    return new Paragraph(line?.replace('Information_Sources', 'Source: Secondary research'));
  }

  if (RESPONSE_BY.OPENAI === type && line?.includes('Oops! No results found')) {
    return new Paragraph({
      children: [
        new TextRun({ text: line }),
        new TextRun({
          text: 'Source: Secondary research',
          break: 1
        })
      ]
    });
  }

  const content = line.trim();
  // Handle Markdown Table
  if (isFirstRow && content === '') {
    rowIndex = 0;
    isFirstRow = false;
  }
  if (isMarkdownTableLine(content) && Array.isArray(sourceSection)) {
    const tableLines: string[] = [content];
    for (let i = ind + 1; i < totalLength; i++) {
      const next = sourceSection[i]?.trim?.();
      if (isMarkdownTableLine(next)) {
        tableLines.push(next);
      } else {
        break;
      }
    }
    rowIndex++;
    const markdownBlock = tableLines.join('\n');
    const parsedTable = parseMarkdownTableToDocxTable(markdownBlock, rowIndex === 1 && !isFirstRow);
    if (parsedTable) return parsedTable;
  }

  if (separatorOnlyRegex.test(content)) {
    isFirstRow = true;
    return [];
  }

  const headerMatch = content.match(/^(#+)\s+(.*)/);
  const spaceMatch = line.match(/ {2}- /);
  const numericDot = line.match(/^\d+([.)])\s*/);

  if (headerMatch) {
    if (ind !== 0) {
      return [
        new Paragraph({
          children: [new TextRun({ text: headerMatch[2], bold: true })]
        }),

        new Paragraph({ text: '' })
      ];
    } else {
      return [
        new Paragraph({
          children: [new TextRun({ text: headerMatch[2], bold: true })]
        })
      ];
    }
  }

  if (numericDot) {
    return new Paragraph({
      indent: {
        left: 420
      },
      children: parseFormattedLineChildren(content.replaceAll(')', '.'))
    });
  }

  if (content.startsWith('- **') && content.endsWith('**')) {
    return new Paragraph({
      bullet: { level: 0 },
      children: parseFormattedLineChildren(content.substring(4).replaceAll(/\*\*/g, ''), true)
    });
  }

  if (spaceMatch) {
    return new Paragraph({
      bullet: { level: 1 },
      children: parseFormattedLineChildren(content.substring(2))
    });
  }

  if (content.startsWith('- ')) {
    return new Paragraph({
      bullet: { level: 0 },
      children: parseFormattedLineChildren(content.substring(2))
    });
  }
  if (RESPONSE_BY.STRATEGY_EDGE === type && totalLength - 1 === ind) {
  // Process the line directly instead of trying to extract from TextRun instances
  const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  const styledChildren: (TextRun | ExternalHyperlink)[] = [];
  
  parts.forEach((part) => {
    const boldMatch = part.match(/^\*\*(.*)\*\*$/);
    if (boldMatch) {
      // Bold text segment
      styledChildren.push(new TextRun({
        text: boldMatch[1],
        bold: true,
        color: isCompletelyAbortedResposne ? '656579' : '',
        italics: isCompletelyAbortedResposne ? true : false
      }));
    } else if (part.trim()) {
      // Regular text segment - check for markdown links
      const linkElements = parseMarkdownLinks(part, false);
      linkElements.forEach(element => {
        if (element instanceof TextRun) {
          styledChildren.push(new TextRun({
            text: (element as any).text || part, // Fallback to original part
            bold: false,
            color: isCompletelyAbortedResposne ? '656579' : '',
            italics: isCompletelyAbortedResposne ? true : false
          }));
        } else {
          styledChildren.push(element); // ExternalHyperlink
        }
      });
    }
  });

  // Fallback if no parts were processed
  if (styledChildren.length === 0) {
    styledChildren.push(new TextRun({
      text: line,
      color: isCompletelyAbortedResposne ? '656579' : '',
      italics: isCompletelyAbortedResposne ? true : false
    }));
  }

  return new Paragraph({
    children: [
      ...styledChildren,
      ...(source === 'Transcripts' ? [new TextRun({ text: '', break: 1 })] : []),
      ...modifiedSource
    ]
  });
}
  if (RESPONSE_BY.SECTOR === type && totalLength - 1 === ind) {
    return new Paragraph({
      children: [
        new TextRun({ text: line }),
        ...modifiedSource,
        new TextRun({ text: '\n', break: 1 })
      ]
    });
  }
  if (RESPONSE_BY.PESPECTIVE === type && totalLength - 1 === ind) {
    return new Paragraph({
      children: [
        new TextRun({ text: line }),
        ...modifiedSource,
        new TextRun({ text: '\n', break: 1 })
      ]
    });
  }

  return new Paragraph({ children: parseFormattedLineChildren(content) });
};

const generateDocument = async (
  data: any,
  userImg: string | undefined,
  source: any,
  checkedCheckboxes?: any,
  eyIPInterRadio?: any,
  validURLinputValue?: any
) => {
  const IMAGES = {
    [RESPONSE_BY.USER]: userImg ? userImg : AppConst.AVATAR_IMAGE,
    [RESPONSE_BY.STRATEGY_EDGE]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.EMBAI]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.EMBROYNIC]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.OPENAI]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.SECTOR]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.SURVEY]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.PESPECTIVE]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.MARKET_REPORT]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.TRANSCRIPTS]: AppConst.USER_AI_IMG
  };
  const paragraphs: any = [];

  const tableRowWithCol = (
    respData: any,
    respImg: any,
    tableContent?: any,
    respType?: any,
    sourceSection?: any
  ) => {
    if (source == 'Emb' || source == 'EmbOpenAI' || source == 'OpenAI') {
      sourceSection = sourceSection.slice(0, 1);
    }
    const totalLength = decodeHTML(respData.summary).split("\n");
    const { length } = totalLength;
    return (
      paragraphs.push(
        new TableRow({
          children: [
            new TableCell({
              borders: noBorders,
              margins: {
                right: 200,
              },
              children: [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: respImg,
                      transformation: {
                        width: 30,
                        height: 30,
                      },
                    }),
                  ],
                }),
              ],
            }),

            new TableCell({
              borders: noBorders,
              children: [
                ...(respType !== 'user'
                  ? decodeHTML(respData.summary)
                      .split('\n')
                      .flatMap((line: string, ind: number) => {
                        const result = generateDocSummary(
                          line,
                          respType,
                          length,
                          ind,
                          respData?.isParitallyAbortedResponse,
                          respData?.isCompletelyAbortedResposne,
                          sourceSection
                        );
                        return Array.isArray(result) ? result : [result];
                      })
                  : [new Paragraph(respData.summary?.replace(/\n\r+$/, '').trim())]),
                ...(tableContent &&
                  source !== 'SectorInsights' &&
                  source !== 'Perspective' &&
                  source !== 'MarketReports' &&
                  source !== 'SurveyInsights' &&
                  source !== 'Transcripts'
                  ? [generateTable(tableContent)]
                  : source === 'SurveyInsights'
                    ? []
                    : source === 'Transcripts'
                      ? [generateTranscriptTable(tableContent, source)]
                      : [generateSectorTable(tableContent, source)]
                )
              ]
            })
          ]
        })
      ),
      (source !== 'Transcripts' && (source !== 'Perspective' || tableContent)) && paragraphs.push(emptyRow)
    );
  };

  data.forEach(async (chat: any, ind: number) => {
    rowIndex = 0;
    isFirstRow = false;
    const { SEResponse, LLMResponse, user } = chat;
    let tableContent = null;
    let sources: string[] | undefined = [];
    const sourceSection =
      SEResponse &&
      handleSourceSection(
        SEResponse?.Sources,
        checkedCheckboxes,
        eyIPInterRadio,
        validURLinputValue
      );
    if (
      SEResponse?.ResponseType === RESPONSE_TYPE.TABLE &&
      SEResponse.Content &&
      SEResponse.Content.every((el: any) => Boolean(el) && typeof el === 'object')
    ) {
      tableContent = prepareContentForTable(SEResponse?.Content || LLMResponse?.Content);
    } else if (
      SEResponse?.relevantFiles &&
      SEResponse?.relevantFiles.every((el: any) => Boolean(el) && typeof el === 'object')
    ) {
      tableContent = SEResponse?.relevantFiles?.map((el: any) => {
        const obj: any = {
          file_name: el.file_name ?? '',
          page_number: el.page_number || [],
          authors: el?.authors || [],
          sector: el?.sector || [],
          geography: el?.geography || [],
          contentAge: el?.contentAge || '',
          source: el?.source || ''
        };
        return obj;
      });
    }
    if (SEResponse || LLMResponse) {
      sources = getSources(SEResponse || LLMResponse);
    }

    if (user) {
      tableRowWithCol(user, IMAGES[RESPONSE_BY.USER], null, RESPONSE_BY.USER, sourceSection);
    }
    if (SEResponse) {
      if (!SEResponse.relevantFiles && source === 'Emb') {
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.EMBROYNIC],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          sourceSection
        );
      } else if (source === 'EmbOpenAI') {
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.EMBAI],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          sourceSection
        );
      } else if (source === 'OpenAI') {
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.OPENAI],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          sourceSection
        );
      } else if (source === 'MarketReports') {
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.MARKET_REPORT],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          sourceSection
        );
      } else if (source === 'Transcripts') {
          tableContent = SEResponse?.relevantFiles?.map((el: any) => {
          const obj: any = {
            sectors: el?.sector || [],
            years: el?.years || [],
          };
          return obj;
        });
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.TRANSCRIPTS],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          sourceSection
        );
      } else {
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.SECTOR],
          tableContent,
          RESPONSE_BY.SECTOR,
          sourceSection
        );
      }
    }
  });

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
             size: "11pt",
            font: "Calibri",
          },
        },
      },
      paragraphStyles: [
        {
          id: "tableHeader",
          run: {
            bold: true,
          },
        },
        {
          id: "summary",
          quickFormat: true,
        },
      ],
    },
    sections: [
      {
        children: [
          new Table({
            width: {
              size: 100,
              type: WidthType.PERCENTAGE
            },
            layout: TableLayoutType.AUTOFIT,
            rows: paragraphs
          })
        ]
      }
    ]
  });
  return doc;
};

const exportToWord = async (
  data: IQuery[],
  userImage?: string,
  source?: any,
  checkedCheckboxes?: any,
  eyIPInterRadio?: any,
  validURLinputValue?: any
) => {
  const clonedData = structuredClone(data);
  const doc = await generateDocument(
    clonedData,
    userImage,
    source,
    checkedCheckboxes,
    eyIPInterRadio,
    validURLinputValue
  );
  const blob = await Packer.toBlob(doc);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = downloadFilenameGenerator(source, 'docx');
  link.click();
};

export default exportToWord;